"use client";

import { motion } from "framer-motion";
import Link from "next/link";
import Image from "next/image";
import { useEffect, useState } from "react";
import { ChevronRight } from "lucide-react";
import { createClient } from "@/lib/supabase/client";

interface Category {
  id: number;
  name: string;
  slug: string;
  parent_id: number | null;
  image_url?: string;
}

interface GroupedCategories {
  [key: string]: Category[];
}

export default function MegaMenu() {
  const [categories, setCategories] = useState<GroupedCategories>({});
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      setLoading(true);
      try {
        const supabase = createClient();
        // Fetch parent categories
        const { data: parentCategories, error: parentError } = await supabase
          .from('categories')
          .select('*')
          .is('parent_id', null);

        if (parentError) throw parentError;

        // Fetch child categories
        const { data: childCategories, error: childError } = await supabase
          .from('categories')
          .select('*')
          .not('parent_id', 'is', null);

        if (childError) throw childError;

        // Group child categories by parent_id
        const grouped: GroupedCategories = {};
        
        if (parentCategories) {
          parentCategories.forEach((parent: Category) => {
            const children = childCategories?.filter(
              (child: Category) => child.parent_id === parent.id
            ) || [];
            grouped[parent.name] = children;
          });
        }

        setCategories(grouped);
      } catch (error) {
        console.error("Error fetching categories:", error);
        // Fallback to sample data if Supabase isn't connected yet
        setCategories({
          "Spirits": [
            { id: 1, name: "Whisky", slug: "whisky", parent_id: 1 },
            { id: 2, name: "Vodka", slug: "vodka", parent_id: 1 },
            { id: 3, name: "Gin", slug: "gin", parent_id: 1 },
            { id: 4, name: "Rum", slug: "rum", parent_id: 1 },
            { id: 5, name: "Tequila", slug: "tequila", parent_id: 1 },
          ],
          "Wine": [
            { id: 6, name: "Red Wine", slug: "red-wine", parent_id: 2 },
            { id: 7, name: "White Wine", slug: "white-wine", parent_id: 2 },
            { id: 8, name: "Rosé", slug: "rose", parent_id: 2 },
          ],
          "Beer": [
            { id: 9, name: "Lager", slug: "lager", parent_id: 3 },
            { id: 10, name: "Ale", slug: "ale", parent_id: 3 },
            { id: 11, name: "Stout", slug: "stout", parent_id: 3 },
          ],
          "Champagne": [
            { id: 12, name: "Brut", slug: "brut", parent_id: 4 },
            { id: 13, name: "Rosé", slug: "champagne-rose", parent_id: 4 },
          ],
        });
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // Add mobile detection
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const checkIfMobile = () => {
        setIsMobile(window.innerWidth < 768);
      };
      
      // Set initial value
      checkIfMobile();
      
      // Add event listener for window resize
      window.addEventListener('resize', checkIfMobile);
      
      // Cleanup
      return () => {
        window.removeEventListener('resize', checkIfMobile);
      };
    }
  }, []);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, y: -5 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.2,
        staggerChildren: 0.05
      }
    },
    exit: { 
      opacity: 0,
      y: -5,
      transition: { duration: 0.2 }
    }
  };

  return (
    <motion.div
      className="absolute left-0 w-screen bg-card shadow-lg rounded-b-md mt-2 border-t"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {isMobile ? (
          // Mobile view with scrollable categories
          <div className="md:hidden max-h-[70vh] overflow-y-auto">
            <div className="grid grid-cols-1 gap-8">
              {Object.entries(categories).map(([groupName, items], index) => (
                <div key={index} className="space-y-4">
                  <Link 
                    href={`/category/${groupName.toLowerCase().replace(/ /g, '-')}`}
                    className="block font-semibold text-lg border-b pb-2 hover:text-primary transition-colors"
                  >
                    {groupName}
                  </Link>
                  <ul className="space-y-3">
                    {items.map((category) => (
                      <motion.li 
                        key={category.id}
                        whileHover={{ x: 5 }}
                        className="transition-colors"
                      >
                        <Link 
                          href={`/category/${category.slug}`}
                          className="flex items-center text-sm text-muted-foreground hover:text-primary"
                        >
                          <ChevronRight className="h-3 w-3 mr-1 flex-shrink-0" />
                          <span className="line-clamp-1">{category.name}</span>
                        </Link>
                      </motion.li>
                    ))}
                  </ul>
                </div>
              ))}
              
              {/* Mobile featured section */}
              <div className="mt-4 bg-muted rounded-md overflow-hidden">
                <div className="relative h-32 w-full">
                  <Image
                    src="https://images.pexels.com/photos/1283219/pexels-photo-1283219.jpeg?auto=compress&cs=tinysrgb&w=600"
                    alt="Featured collection"
                    fill
                    className="object-cover"
                  />
                </div>
                <div className="p-3">
                  <h4 className="font-medium text-sm mb-1">Premium Collection</h4>
                  <Link
                    href="/products"
                    className="text-xs font-medium text-primary hover:underline"
                  >
                    Browse Collection
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Desktop view
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Categories columns */}
            <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-3 gap-8">
              {loading ? (
                <div className="col-span-full flex justify-center py-8">
                  <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin"></div>
                </div>
              ) : (
                Object.entries(categories).map(([groupName, items], index) => (
                  <div key={index} className="space-y-4">
                    <Link 
                      href={`/category/${groupName.toLowerCase().replace(/ /g, '-')}`}
                      className="block font-semibold text-lg border-b pb-2 hover:text-primary transition-colors"
                    >
                      {groupName}
                    </Link>
                    <ul className="space-y-2">
                      {items.map((category) => (
                        <motion.li 
                          key={category.id}
                          whileHover={{ x: 5 }}
                          className="transition-colors"
                        >
                          <Link 
                            href={`/category/${category.slug}`}
                            className="flex items-center text-sm text-muted-foreground hover:text-primary"
                          >
                            <ChevronRight className="h-3 w-3 mr-1" />
                            {category.name}
                          </Link>
                        </motion.li>
                      ))}
                    </ul>
                  </div>
                ))
              )}
            </div>
            
            {/* Featured category */}
            <div className="md:col-span-1">
              <div className="bg-muted rounded-md overflow-hidden h-full">
                <h3 className="font-semibold text-lg p-4">Featured</h3>
                <div className="relative h-48 w-full">
                  <Image
                    src="https://images.pexels.com/photos/1283219/pexels-photo-1283219.jpeg?auto=compress&cs=tinysrgb&w=600"
                    alt="Featured whisky collection"
                    fill
                    className="object-cover"
                  />
                </div>
                <div className="p-4">
                  <h4 className="font-medium text-sm mb-2">Premium Whisky Collection</h4>
                  <p className="text-xs text-muted-foreground mb-3">
                    Discover our curated selection of the finest whiskies from around the world.
                  </p>
                  <Link
                    href="/category/whisky"
                    className="text-xs font-medium text-primary hover:underline"
                  >
                    Browse Collection
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}